import { useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Button, DropdownButton, Flex, Select, Text } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { useAppContext } from "~/contexts/appContext";
import { ReportTemplateVersionStatus } from "~/graphql/supergraphEnums";
import { formatUserName } from "~/pages/Reporting/utils";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { SPACING } from "~/styles/variables";
import { toDbId, toGlobalId } from "~/utils/graphql-helpers";
import { hasGlobalReportingAccess } from "~/utils/userRoles";
import {
  PreviewDateParameterFields,
  type DateParameterFieldsState,
} from "../../dateParameters";
import { ArenaSelect } from "../../ReportTemplateEditor/components/ArenaSelect";
import { useArenaOptions } from "../../ReportTemplateEditor/hooks/useArenaOptions";
import { usePreviewEntities } from "../../ReportTemplateEditor/hooks/usePreviewEntities";
import { usePreviewTransactionBatches } from "../../ReportTemplateEditor/hooks/usePreviewTransactionBatches";
import {
  getReportTypeLabel,
  type PreviewEntityInfo,
  type PreviewSelectOption,
} from "../../ReportTemplateEditor/types";
import * as styles from "../styles_vanilla.css";
import { TemplateVersionStatus } from "./TemplateVersionStatus";
import type { GetReportTemplateQuery } from "~/graphql/supergraphTypes";

type GqlReportTemplate = NonNullable<GetReportTemplateQuery["reportTemplate"]>;
type GqlCurrentVersion = NonNullable<GqlReportTemplate["currentVersion"]>;

type GqlReview = NonNullable<GqlCurrentVersion["review"]>;

type TemplateData = Pick<
  GqlReportTemplate,
  | "id"
  | "name"
  | "reportType"
  | "arenaId"
  | "updatedAt"
  | "updatedByUser"
  | "archivedAt"
  | "archivedByUser"
> & {
  dataPackageName: string | null;
  currentVersion:
    | (Pick<GqlCurrentVersion, "version" | "status" | "downloadUrl"> & {
        review: GqlReview | null;
      })
    | null;
};

export type { PreviewEntityInfo };

export type TemplateDetailsSidebarProps = {
  template: TemplateData;
  isSystemTemplate: boolean;
  duplicateArenaId?: number | null;
  onArchiveClick: () => void;
  onUnarchiveClick: () => void;
  onRenameClick: () => void;
  selectedEntityId: string;
  selectedAccountId: string;
  accounts: PreviewSelectOption[];
  accountsLoading: boolean;
  onEntityChange: (entity: PreviewEntityInfo | null) => void;
  onAccountChange: (id: string) => void;
  onPreviewArenaNameChange?: (name: string) => void;
  /** Federation global ID of the arena entities/accounts are being previewed against. */
  onPreviewArenaGlobalIdChange?: (globalId: string) => void;
  /** Dates the template's query requires at run time, from `usePreviewDateParameters`. */
  dateParameters: DateParameterFieldsState;
  /** Whether the report type is transaction-based (Capital Call, Distribution, etc.).
   * Transaction-based reports select multiple entities + transactions instead of a
   * single entity + account. */
  isTransactionBased: boolean;
  /** Multi-entity selection (relay IDs) — used by transaction-based reports. */
  selectedEntityIds: string[];
  onEntitiesChange: (ids: string[]) => void;
  /** Selected GL transaction IDs — used by transaction-based reports. */
  selectedTransactionIds: string[];
  onTransactionsChange: (ids: string[]) => void;
  /** Report query ID — forwarded to the GL transaction data query. */
  queryId: number | null;
};

export const TemplateDetailsSidebar = ({
  template,
  isSystemTemplate,
  duplicateArenaId,
  onArchiveClick,
  onUnarchiveClick,
  onRenameClick,
  selectedEntityId,
  selectedAccountId,
  accounts,
  accountsLoading,
  onEntityChange,
  onAccountChange,
  onPreviewArenaNameChange,
  onPreviewArenaGlobalIdChange,
  dateParameters,
  isTransactionBased,
  selectedEntityIds,
  onEntitiesChange,
  selectedTransactionIds,
  onTransactionsChange,
  queryId,
}: TemplateDetailsSidebarProps) => {
  const history = useHistory();
  const { user } = useAppContext();
  const canManageSystemTemplates = hasGlobalReportingAccess(user.roles);
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();

  const versionStatus = template.currentVersion?.status ?? ReportTemplateVersionStatus.Pending;
  const isArchived = template.archivedAt != null;
  const arenaId = template.arenaId;

  const { arenas, arenasLoading, selectedArenaId, setSelectedArenaId } =
    useArenaOptions(!isSystemTemplate);

  const effectiveArenaGlobalId = isSystemTemplate
    ? selectedArenaId != null
      ? toGlobalId("ArenaObjectType", selectedArenaId)
      : ""
    : arenaId != null
      ? toGlobalId("ArenaObjectType", arenaId)
      : "";

  useEffect(() => {
    onPreviewArenaGlobalIdChange?.(effectiveArenaGlobalId);
  }, [effectiveArenaGlobalId, onPreviewArenaGlobalIdChange]);

  const { entities, loading: entitiesLoading } = usePreviewEntities(effectiveArenaGlobalId);

  const arenaDbId = effectiveArenaGlobalId ? Number(toDbId(effectiveArenaGlobalId)) : null;

  const { batches: transactionBatches, loading: transactionBatchesLoading } =
    usePreviewTransactionBatches(selectedEntityIds, arenaDbId, queryId);

  // Auto-selecting the first entity only applies to the single-entity
  // account-statement path. Transaction-based reports leave selection to the user.
  const initializedRef = useRef(false);
  useEffect(() => {
    if (
      !isTransactionBased &&
      !initializedRef.current &&
      !entitiesLoading &&
      entities.length > 0 &&
      !selectedEntityId
    ) {
      initializedRef.current = true;
      onEntityChange(entities[0]);
    }
  }, [isTransactionBased, entitiesLoading, entities, selectedEntityId, onEntityChange]);

  const handleArenaChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const raw = Array.isArray(value) ? value[0] : value;
      if (raw === undefined || raw === "") {
        setSelectedArenaId(null);
        onPreviewArenaNameChange?.("");
      } else {
        const id = Number(raw);
        setSelectedArenaId(Number.isNaN(id) ? null : id);
        const arena = arenas.find((a) => a.id === id);
        onPreviewArenaNameChange?.(arena?.name ?? "");
      }
      onEntityChange(null);
      onAccountChange("");
      onEntitiesChange([]);
      onTransactionsChange([]);
      initializedRef.current = false;
    },
    [
      setSelectedArenaId,
      onEntityChange,
      onAccountChange,
      onEntitiesChange,
      onTransactionsChange,
      onPreviewArenaNameChange,
      arenas,
    ],
  );

  const handleEntityChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const raw = Array.isArray(value) ? value[0] : value;
      const entityId = String(raw ?? "");
      const entity = entities.find((e) => e.id === entityId);
      onEntityChange(entity ?? null);
      onAccountChange("");
    },
    [onEntityChange, onAccountChange, entities],
  );

  const handleEntitiesChange = useCallback(
    (value: string | number | string[] | number[]) => {
      // Clear transaction and account selection when the entity set changes:
      // the recipient account list is scoped to the first selected entity.
      onTransactionsChange([]);
      onAccountChange("");
      onEntitiesChange(Array.isArray(value) ? value.map(String) : [String(value)]);
    },
    [onEntitiesChange, onTransactionsChange, onAccountChange],
  );

  const handleTransactionsChange = useCallback(
    (value: string | number | string[] | number[]) => {
      onTransactionsChange(Array.isArray(value) ? value.map(String) : [String(value)]);
    },
    [onTransactionsChange],
  );

  const handleAccountChange = useCallback(
    (value: string | number | string[] | number[]) => {
      const raw = Array.isArray(value) ? value[0] : value;
      onAccountChange(String(raw ?? ""));
    },
    [onAccountChange],
  );

  const handleDuplicate = useCallback(() => {
    const targetArenaId = duplicateArenaId ?? arenaId ?? "system";
    const prefix = `/reporting/${targetArenaId}/templates`;
    history.push(`${prefix}/duplicate?templateId=${template.id}`);
  }, [history, arenaId, duplicateArenaId, template.id]);

  const handleRequestUpdate = useCallback(() => {
    history.push(`/reporting/${arenaId ?? "system"}/templates/${template.id}`);
  }, [history, arenaId, template.id]);

  const handleDownload = useCallback(() => {
    const url = template.currentVersion?.downloadUrl;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [template.currentVersion?.downloadUrl]);

  const updatedByName = formatUserName(template.updatedByUser);
  const updatedDate = template.updatedAt
    ? formatDateWithTimezone(template.updatedAt, "MMM d, yyyy h:mm a")
    : "";
  const lastUpdatedDisplay = [updatedDate, updatedByName ? `by ${updatedByName}` : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <Flex flexDirection="column" className={styles.sidebar}>
      <Flex flexDirection="column" gap={SPACING.MEDIUM}>
        <Text variant="h4">Template details</Text>
        <Flex flexDirection="column" gap={SPACING.MEDIUM}>
          <Flex alignItems="flex-start" gap={SPACING.SMALL}>
            <Text variant="strong-md" className={styles.detailLabel}>
              Report type
            </Text>
            <Text variant="regular-md">{getReportTypeLabel(template.reportType)}</Text>
          </Flex>

          <Flex alignItems="flex-start" gap={SPACING.SMALL}>
            <Text variant="strong-md" className={styles.detailLabel}>
              Data package
            </Text>
            <Text variant="regular-md">{template.dataPackageName ?? "--"}</Text>
          </Flex>
          <Flex alignItems="flex-start" gap={SPACING.SMALL}>
            <Text variant="strong-md" className={styles.detailLabel}>
              Last updated
            </Text>
            <Text variant="regular-md">{lastUpdatedDisplay}</Text>
          </Flex>

          {(!isSystemTemplate || isArchived) && (
            <Flex alignItems="flex-start" gap={SPACING.SMALL}>
              <Text variant="strong-md" className={styles.detailLabel}>
                Status
              </Text>
              <TemplateVersionStatus
                versionStatus={versionStatus}
                isArchived={isArchived}
                statusByUser={
                  isArchived
                    ? template.archivedByUser
                    : (template.currentVersion?.review?.reviewedByUser ?? null)
                }
                statusAt={
                  isArchived
                    ? (template.archivedAt ?? undefined)
                    : (template.currentVersion?.review?.reviewedAt ?? undefined)
                }
                formatDate={(date) => formatDateWithTimezone(date, "MMM d, yyyy", false)}
                showDeniedComment
                deniedReview={template.currentVersion?.review}
              />
            </Flex>
          )}
        </Flex>
      </Flex>

      <div className={styles.sectionDivider} />

      <Flex flexDirection="column" gap={SPACING.SMALL}>
        <Text variant="h4">Preview settings</Text>
        <div className={styles.selectsContainer}>
          <Flex flexDirection="column" gap={SPACING.MEDIUM}>
            {isSystemTemplate && (
              <ArenaSelect
                arenas={arenas}
                arenasLoading={arenasLoading}
                selectedArenaId={selectedArenaId}
                onChange={handleArenaChange}
              />
            )}
            {isTransactionBased ? (
              <>
                <Select
                  label="Entity"
                  placeholder="Select entities"
                  full
                  multiple
                  matchFromStart={false}
                  disabled={entitiesLoading || (isSystemTemplate && selectedArenaId == null)}
                  value={selectedEntityIds}
                  onChange={handleEntitiesChange}
                >
                  <Select.Section>
                    {entities.map((entity) => (
                      <Select.Option key={entity.id} value={entity.id}>
                        {entity.name}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
                <Select
                  label="Transactions"
                  placeholder="Select transactions"
                  full
                  multiple
                  disabled={transactionBatchesLoading || selectedEntityIds.length === 0}
                  value={selectedTransactionIds}
                  onChange={handleTransactionsChange}
                >
                  <Select.Section>
                    {transactionBatches.map((batch) => (
                      <Select.Option key={batch.id} value={batch.id}>
                        {batch.label}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
                <Select
                  label="Account"
                  placeholder="Select account"
                  full
                  disabled={accountsLoading || selectedEntityIds.length === 0}
                  value={selectedAccountId}
                  onChange={handleAccountChange}
                >
                  <Select.Section>
                    {accounts.map((account) => (
                      <Select.Option key={account.id} value={account.id}>
                        {account.name}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
              </>
            ) : (
              <>
                <Select
                  label="Entity"
                  placeholder="Select entity"
                  full
                  matchFromStart={false}
                  disabled={entitiesLoading || (isSystemTemplate && selectedArenaId == null)}
                  value={selectedEntityId}
                  onChange={handleEntityChange}
                >
                  <Select.Section>
                    {entities.map((entity) => (
                      <Select.Option key={entity.id} value={entity.id}>
                        {entity.name}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
                <Select
                  label="Account"
                  placeholder="Select account"
                  full
                  disabled={accountsLoading || !selectedEntityId}
                  value={selectedAccountId}
                  onChange={handleAccountChange}
                >
                  <Select.Section>
                    {accounts.map((account) => (
                      <Select.Option key={account.id} value={account.id}>
                        {account.name}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
              </>
            )}
            <PreviewDateParameterFields dateParameters={dateParameters} />
          </Flex>
        </div>
      </Flex>

      <div className={styles.actionsContainer}>
        <Flex gap={SPACING.SMALL} justifyContent="flex-end" alignItems="center">
          {isArchived ? (
            <>
              <DropdownButton
                icon="ellipsis-h"
                align="right"
                data-testid="template-actions-menu"
                usePortal
                direction="up"
              >
                <DropdownButton.Section name="">
                  {(isSystemTemplate || template.arenaId == null) &&
                  !canManageSystemTemplates ? (
                    <DropdownButton.Action onSelect={handleDuplicate}>
                      Duplicate template
                    </DropdownButton.Action>
                  ) : (
                    <DropdownButton.Action onSelect={onUnarchiveClick}>
                      Restore template
                    </DropdownButton.Action>
                  )}
                </DropdownButton.Section>
              </DropdownButton>
              <Button
                variant="primary"
                icon="download"
                onClick={handleDownload}
                disabled={!template.currentVersion?.downloadUrl}
              >
                Download word template
              </Button>
            </>
          ) : (
            <>
              <DropdownButton
                icon="ellipsis-h"
                align="right"
                data-testid="template-actions-menu"
                usePortal
                direction="up"
              >
                {(isSystemTemplate || template.arenaId == null) && !canManageSystemTemplates ? (
                  <DropdownButton.Section name="">
                    <DropdownButton.Action key="duplicate" onSelect={handleDuplicate}>
                      Duplicate template
                    </DropdownButton.Action>
                  </DropdownButton.Section>
                ) : (
                  [
                    <DropdownButton.Section key="main" name="" hasBottomDivider>
                      {[
                        ...(versionStatus !== ReportTemplateVersionStatus.Approved
                          ? [
                              <DropdownButton.Action key="download" onSelect={handleDownload}>
                                Download word template
                              </DropdownButton.Action>,
                            ]
                          : []),
                        <DropdownButton.Action key="duplicate" onSelect={handleDuplicate}>
                          Duplicate template
                        </DropdownButton.Action>,
                        <DropdownButton.Action key="rename" onSelect={onRenameClick}>
                          Update template name
                        </DropdownButton.Action>,
                      ]}
                    </DropdownButton.Section>,
                    <DropdownButton.Section key="danger" name="">
                      <DropdownButton.Action onSelect={onArchiveClick} color={Colors.Danger600}>
                        Archive template
                      </DropdownButton.Action>
                    </DropdownButton.Section>,
                  ]
                )}
              </DropdownButton>

              {versionStatus === ReportTemplateVersionStatus.Approved ? (
                <Button
                  variant="primary"
                  icon="download"
                  onClick={handleDownload}
                  disabled={!template.currentVersion?.downloadUrl}
                >
                  Download word template
                </Button>
              ) : (
                <Button variant="primary" onClick={handleRequestUpdate}>
                  Update template
                </Button>
              )}
            </>
          )}
        </Flex>
      </div>
    </Flex>
  );
};
